import React, { useState, useEffect } from 'react';
import { FaUsers, FaClipboardList, FaChartLine, FaCrown } from 'react-icons/fa';
import EducationStatsChart from '../components/EducationStatsChart';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { FaGraduationCap, FaChartBar, FaMedal, FaPlusCircle } from 'react-icons/fa';
import { Link } from 'react-router-dom';

function EducationStatsBox() {
  const [stats, setStats] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    fetch("https://dev.sliitleo.org/Backend/get_education_statistics.php")
      .then(res => res.json())
      .then(data => {
        if (data.success) setStats(data.stats);
        else setError("Failed to load statistics");
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load statistics");
        setLoading(false);
      });
  }, []);

  if (loading) return <div className="text-gray-400">Loading statistics...</div>;
  if (error) return <div className="text-red-400">{error}</div>;
  if (!stats) return null;

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 md:gap-6 mt-4 sm:mt-6">
        {/* Total Participants */}
        <div className="bg-[#181c23] rounded-xl border border-[#23272f] shadow-md flex items-center p-4 sm:px-5 sm:py-5">
          <div className="mr-3 sm:mr-4 text-pink-400 text-2xl sm:text-3xl"><FaUsers /></div>
          <div>
            <div className="text-2xl sm:text-3xl font-bold text-white leading-none">{stats.total_users}</div>
            <div className="flex items-center gap-1.5 sm:gap-2 mt-0.5 sm:mt-1 text-gray-400 text-sm sm:text-base">
              <span className="text-sm sm:text-lg"><svg width="20" height="20" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="#a1a1aa" strokeWidth="2" /><circle cx="12" cy="12" r="5" fill="#a1a1aa" /></svg></span>
              <span className="truncate">Total Participants</span>
            </div>
          </div>
        </div>
        {/* Quizzes Completed */}
        <div className="flex-1 min-w-[220px] bg-[#181c23] rounded-xl border border-[#23272f] shadow-md flex items-center px-6 py-5">
          <div className="mr-4 text-green-400 text-3xl"><FaClipboardList /></div>
          <div>
            <div className="text-3xl font-bold text-white leading-none">{stats.total_completed_quizzes}</div>
            <div className="flex items-center gap-2 mt-1 text-gray-400 text-base">
              <span className="text-lg"><svg width="20" height="20" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="#22c55e" strokeWidth="2" /><path d="M9 12l2 2 4-4" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></span>
              Quizzes Completed
            </div>
          </div>
        </div>
        {/* Average Score */}
        <div className="flex-1 min-w-[220px] bg-[#181c23] rounded-xl border border-[#23272f] shadow-md flex items-center px-6 py-5">
          <div className="mr-4 text-blue-400 text-3xl"><FaChartLine /></div>
          <div>
            <div className="text-3xl font-bold text-white leading-none">{stats.average_score}</div>
            <div className="flex items-center gap-2 mt-1 text-gray-400 text-base">
              <span className="text-lg"><svg width="20" height="20" fill="none" viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="16" rx="4" stroke="#60a5fa" strokeWidth="2" /><path d="M7 13l3 3 7-7" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></span>
              Average Score
            </div>
          </div>
        </div>
        {/* Highest Score */}
        <div className="flex-1 min-w-[220px] bg-[#181c23] rounded-xl border border-[#23272f] shadow-md flex items-center px-6 py-5">
          <div className="mr-4 text-yellow-400 text-3xl"><FaCrown /></div>
          <div>
            <div className="text-3xl font-bold text-white leading-none">{stats.highest_score}</div>
            <div className="flex items-center gap-2 mt-1 text-gray-400 text-base">
              <span className="text-lg"><svg width="20" height="20" fill="none" viewBox="0 0 24 24"><path d="M4 16l4-8 4 8 4-8 4 8" stroke="#facc15" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></span>
              Highest Score
            </div>
          </div>
        </div>
        {stats && <EducationStatsChart stats={stats} />}
      </div>
    </> // Added closing JSX fragment tag here
  );
}

function Education() {
  const user = JSON.parse(localStorage.getItem('user')) || {
    name: 'Unknown User',
    role: 'Member',
    image: '/default-user.png',
  };
  return (
    <div className="flex flex-col md:flex-row bg-[#111827] text-white min-h-screen">
      <Sidebar className="hidden md:block" />
      <Topbar user={user} />
      <main className="md:ml-64 mt-14 w-full px-4 sm:px-6 md:px-8 py-6 md:py-10">
        <div className="backdrop-blur-md bg-gradient-to-r from-[#23272f]/80 to-[#181b20]/80 border border-gray-800 rounded-2xl p-4 md:p-5 mb-6 md:mb-8 flex items-center gap-3 md:gap-4 shadow-lg">
          <span className="flex items-center gap-2 md:gap-3">
            <span className="bg-indigo-600 p-1.5 md:p-2 rounded-full shadow-lg flex items-center justify-center">
              <FaGraduationCap className="text-white text-xl md:text-2xl" />
            </span>
            <span className="text-base md:text-lg font-bold tracking-wide text-white drop-shadow">Education Admin<span className="text-gray-400 font-normal"> / Statistics</span></span>
          </span>
        </div>

        <div className="mb-6 md:mb-8">
          <div className="flex flex-wrap gap-2 sm:gap-3 mt-2 sm:mt-4">
            <Link to="/education-statistics" className="flex-1 sm:flex-none flex items-center justify-center gap-1 sm:gap-2 bg-[#23272f] px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 rounded-lg sm:rounded-xl text-sm sm:text-base font-semibold text-white border border-gray-700 shadow hover:scale-[1.02] sm:hover:scale-105 transition focus:outline-none focus:ring-2 focus:ring-orange-400 group">
              <span className="text-orange-400 text-lg sm:text-xl"><FaChartBar /></span>
              <span className="truncate">Statistics</span>
            </Link>
            <Link to="/education-leaderboard" className="flex-1 sm:flex-none flex items-center justify-center gap-1 sm:gap-2 bg-[#23272f] px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 rounded-lg sm:rounded-xl text-sm sm:text-base font-semibold text-white border border-gray-700 shadow hover:scale-[1.02] sm:hover:scale-105 transition focus:outline-none focus:ring-2 focus:ring-blue-400 group">
              <span className="text-blue-400 text-lg sm:text-xl"><FaMedal /></span>
              <span className="truncate">Leaderboard</span>
            </Link>
            <Link to="/education-add-quiz-question" className="flex-1 sm:flex-none flex items-center justify-center gap-1 sm:gap-2 bg-[#23272f] px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 rounded-lg sm:rounded-xl text-sm sm:text-base font-semibold text-white border border-gray-700 shadow hover:scale-[1.02] sm:hover:scale-105 transition focus:outline-none focus:ring-2 focus:ring-red-400 group">
              <span className="text-red-400 text-lg sm:text-xl"><FaPlusCircle /></span>
              <span className="truncate">Add Quiz</span>
            </Link>
          </div>
        </div>

        <div className="bg-[#23272f] rounded-xl p-4 sm:p-6 md:p-8 shadow-lg border border-gray-800">
          <h2 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 text-white">Education Statistics</h2>
          <EducationStatsBox />
        </div>
      </main>
    </div>
  );
}

export default Education;
